import { FF_CONSULTATION_CHARGES_UPLIFT } from '@/constants/featureFlags';
import { getAuthData } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import { useConsult } from '@/hooks/consult/useConsult';
import type { OnSaveConsultationOptions } from '@/pages/Patient';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import { Box, Button, Stack } from '@mui/material';
import type { ConsultationStatusSelectorProps } from '../ConsultationStatusSelector';
import { ConsultationStatusSelector } from '../ConsultationStatusSelector';
import type { ShowConsultation } from '../ShowConsultation';
import { NewStyledActions, StyledContainer } from './NurseConsultActions.styles';

interface NurseConsultActionsProps
  extends Pick<
    ConsultationStatusSelectorProps,
    'consultationStatuses' | 'handleStatusChange' | 'handleSubStatusChange'
  > {
  showConsultation: ShowConsultation;
  patientActive: boolean;
  handleSubmit: () => void;
  handleSaveConsultation: (params?: OnSaveConsultationOptions) => Promise<void>;
}

export const NurseConsultActions = ({
  showConsultation,
  patientActive,
  handleSubmit,
  handleSaveConsultation,
  consultationStatuses,
  handleStatusChange,
  handleSubStatusChange,
}: NurseConsultActionsProps) => {
  const { isDoc } = getAuthData();
  const { flags } = useFeatureFlags();
  const ffConsultationChargesUplift = flags[FF_CONSULTATION_CHARGES_UPLIFT];

  const { paymentState, paymentMethod } = useConsult({
    paymentStatus: showConsultation?.payment_status,
    paymentDetails: showConsultation?.paymentStatus || {},
    paidDate: showConsultation?.paid_date ?? '',
    paymentQueued: showConsultation?.payment_queued,
    invoiceCode: showConsultation?.xero_consultation_code,
  });

  const isSubmitButtonDisabled =
    !patientActive || isDoc || (paymentMethod === 'INVOICE' && paymentState === 'NOT_PAID');
  const isStatusSelectorDisabled = !patientActive || isDoc;

  if (ffConsultationChargesUplift) {
    return (
      <StyledContainer>
        <SectionHeader headerText="Consultation Status" />
        <NewStyledActions>
          <ConsultationStatusSelector
            statusId={showConsultation.status_id}
            subStatusId={showConsultation.sub_status_id}
            consultationStatuses={consultationStatuses}
            handleStatusChange={handleStatusChange}
            handleSubStatusChange={handleSubStatusChange}
            disabled={isStatusSelectorDisabled}
          />
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ maxHeight: '36px', marginTop: '8px' }}
            disabled={isSubmitButtonDisabled}
            onClick={handleSubmit}
          >
            <Box width="100px">Submit</Box>
          </Button>
        </NewStyledActions>
      </StyledContainer>
    );
  }

  return (
    <>
      <SectionHeader headerText="Actions" />
      <Stack direction="row" alignItems="stretch" justifyContent="flex-end" spacing={4} my={4}>
        <ConsultationStatusSelector
          statusId={showConsultation.status_id}
          subStatusId={showConsultation.sub_status_id}
          consultationStatuses={consultationStatuses}
          handleStatusChange={handleStatusChange}
          handleSubStatusChange={handleSubStatusChange}
          disabled={isStatusSelectorDisabled}
        />
        <Button
          variant="outlined"
          color="primary"
          id="consultation-save-btn"
          disabled={!patientActive || isDoc}
          onClick={() => handleSaveConsultation({ skipAssessment: false })}
        >
          Save
        </Button>
        <Button variant="contained" color="primary" disabled={!patientActive || isDoc} onClick={handleSubmit}>
          Submit
        </Button>
      </Stack>
    </>
  );
};
